import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { login, loginTotp } from '../api.js';

type Stage = 'credentials' | 'totp';

export function LoginPage() {
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setPendingToken = useAuthStore((s) => s.setPendingToken);

  const stage1 = useMutation({
    mutationFn: async () => login(email, password),
    onSuccess: (data) => {
      if (data.status === 'totp_setup_required') {
        setPendingToken(data.setup_token, 'totp_setup');
        void navigate({ to: '/setup-totp' });
      } else {
        setChallengeToken(data.challenge_token);
        setStage('totp');
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const stage2 = useMutation({
    mutationFn: async () => loginTotp(challengeToken, code),
    onSuccess: (data) => {
      setUser(data.user);
      void navigate({ to: '/' });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (stage === 'credentials') stage1.mutate();
    else stage2.mutate();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-n-50 p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-[400px] rounded-lg border border-n-200 bg-white p-8 shadow-md"
      >
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="flex h-[56px] w-[56px] items-center justify-center rounded-lg bg-brand-500 text-white">
            <ShieldCheck size={28} />
          </div>
          <div className="text-center">
            <h1 className="text-[20px] font-bold text-n-900">ChatPlus Super</h1>
            <p className="text-[13px] text-n-500">Painel administrativo</p>
          </div>
        </div>

        {stage === 'credentials' ? (
          <div className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            <Input
              label="Senha"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" size="lg" className="w-full mt-2" loading={stage1.isPending}>
              Entrar
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-md bg-brand-50 px-3 py-2 text-[12px] text-brand-700">
              Abra seu app autenticador e digite o código de 6 dígitos.
            </div>
            <Input
              label="Código TOTP"
              type="text"
              inputMode="numeric"
              maxLength={6}
              pattern="\d{6}"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              autoFocus
              className="text-center tracking-[0.5em] font-mono text-[18px]"
            />
            <Button type="submit" size="lg" className="w-full mt-2" loading={stage2.isPending}>
              Verificar
            </Button>
            <button
              type="button"
              onClick={() => {
                setStage('credentials');
                setCode('');
                setChallengeToken('');
              }}
              className="text-[12px] text-n-500 hover:text-n-700"
            >
              ← Voltar
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
